import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import Footer from './Footer';
import Logo from './marketing/Logo';

interface LotAdmin {
  lot_id: string;
  role: string;
  lot: {
    id: string;
    slug: string;
    display_name: string;
    status: string;
  }[];
}

function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast.error('Invalid credentials');
        return;
      }

      if (!data.user) {
        toast.error('Authentication failed');
        return;
      }

      // Check if user is using a temporary password
      const userMetadata = data.user.user_metadata;
      if (userMetadata && userMetadata.temporary_password === true) {
        toast.success('Please update your temporary password to continue');
        navigate('/admin/force-password-change');
        return;
      }

      // Check if user has admin access to any lots using secure function
      // The function uses auth.uid() by default, so no parameters needed
      const { data: lotAccess, error: accessError } = await supabase
        .rpc('get_user_lot_access');

      if (accessError) {
        console.error('Error checking lot access:', accessError);
        toast.error('Error verifying permissions');
        await supabase.auth.signOut();
        return;
      }

      if (!lotAccess || lotAccess.length === 0) {
        toast.error('Access denied - No lot permissions found');
        await supabase.auth.signOut();
        return;
      }

      // Transform the response to match our interface
      const adminLots = lotAccess.map((access: any) => ({
        lot_id: access.lot_id,
        role: access.role,
        lot: [{
          id: access.lot_id,
          slug: access.lot_slug,
          display_name: access.lot_display_name,
          status: access.lot_status
        }]
      })) as LotAdmin[];
      
      // If admin manages multiple lots, redirect to lot selection
      if (adminLots.length > 1) {
        toast.success('Welcome back!');
        navigate('/admin/lot-selection', { 
          state: { lots: adminLots } 
        });
      } else {
        // Single lot admin - redirect directly to their lot's dashboard
        const lot = adminLots[0].lot[0]; // Access first element of lot array
        toast.success(`Welcome back to ${lot.display_name}!`);
        navigate(`/admin/lots/${lot.slug}/dashboard`);
      }
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      
              console.log('Requesting password reset for:', email);

              const response = await fetch('/.netlify/functions/reset-temp-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
                  body: JSON.stringify({ 
            email,
            flow_type: 'forgot_password' 
          }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send password reset email');
      }

        setResetEmailSent(true);
      toast.success(data.message || 'Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(`Password reset failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Logo size={28} />
          </div>
          <p className="mt-2 text-ink-500">Admin Management</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-700">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-ink-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-confetti-pink"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-ink-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-confetti-pink"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-confetti-pink text-white py-2 px-4 rounded-lg hover:bg-confetti-pink/90 transition duration-200 disabled:opacity-50"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-confetti-pink hover:text-confetti-pink/80 hover:underline"
            >
              Forgot your password?
            </button>
          </div>
        </form>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Reset Password</h2>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmailSent(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {resetEmailSent ? (
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Reset Email Sent</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    We've sent a password reset link to <strong>{email}</strong>. Check your inbox and follow the instructions.
                  </p>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmailSent(false);
                    }}
                    className="w-full bg-confetti-pink text-white py-2 px-4 rounded-lg hover:bg-confetti-pink/90 transition duration-200"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword}>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-confetti-pink"
                      placeholder="Enter your email address"
                      disabled={loading}
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmailSent(false);
                      }}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-confetti-pink text-white rounded-lg hover:bg-confetti-pink/90 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Sending...' : 'Send Reset Email'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-ink-500">Don't have an account?</span>
          </div>
        </div>

        {/* Lot Owner Signup Section */}
        <div className="bg-confetti-pink-soft p-4 rounded-lg border border-confetti-pink/30">
          <div className="flex items-center mb-2">
            <Building2 className="h-5 w-5 text-confetti-pink mr-2" />
            <h3 className="font-medium text-ink-900">Own a Parking Lot?</h3>
          </div>
          <p className="text-sm text-ink-700 mb-3">
            Join ParkConfetti and start accepting online payments from your customers in minutes.
          </p>
          <button
            onClick={() => navigate('/signup/lot-owner')}
            className="w-full bg-confetti-pink text-white py-2 px-4 rounded-lg hover:bg-confetti-pink/90 transition duration-200 flex items-center justify-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Sign Up as Lot Owner
          </button>
        </div>

        <Footer />
      </div>
    </div>
  );
}

export default AdminLogin;